<template>
  <!-- One grid of equally wide columns: featured, the entry list and every group each take a column (a group with
       more than 10 links spans several). -->
  <div
    class="page-container grid grid-flow-col justify-start gap-8 pt-4 pb-8"
    :style="{ '--mega-menu-column': COLUMN_WIDTH, gridAutoColumns: 'var(--mega-menu-column)' }">
    <div v-if="featuredGroups.length" class="flex flex-col gap-6 pr-8 border-r border-border">
      <MegaMenuGroup
        v-for="group in featuredGroups"
        :key="group.label"
        :label="group.label"
        :links="group.links"
        :columns="group.columns"
        @navigate="emit('navigate')" />
    </div>

    <!-- Drilldown: a list of entries (countries, sports) on the left, the hovered entry's groups beside it. -->
    <template v-if="isDrilldown">
      <MegaMenuEntryList
        :label="entriesLabel"
        :entries="drilldownEntries"
        :active-label="activeEntry?.label"
        @activate="activeLabel = $event.label"
        @navigate="emit('navigate')" />
      <!-- Hovering another entry fades its groups in, with the timing and easing of moving between levels in the mobile
           menu (without the slide). They sit in one block spanning their columns, lined up with the panel's. -->
      <Transition
        mode="out-in"
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        leave-active-class="transition duration-150 ease-in"
        leave-to-class="opacity-0">
        <div
          v-if="activeEntry"
          :key="activeEntry.label"
          class="grid grid-flow-col gap-8"
          :style="{ gridColumn: `span ${activeColumns}`, gridAutoColumns: 'var(--mega-menu-column)' }">
          <MegaMenuGroup
            v-for="group in activeGroups"
            :key="group.label"
            :label="group.label"
            :links="group.links"
            :columns="group.columns"
            @navigate="emit('navigate')" />
        </div>
      </Transition>
    </template>

    <template v-else>
      <MegaMenuGroup
        v-for="group in toGroups(visibleEntries, groupsLabel)"
        :key="group.label"
        :label="group.label"
        :links="group.links"
        :columns="group.columns"
        @navigate="emit('navigate')" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import MegaMenuGroup from "./MegaMenuGroup.vue";
import MegaMenuEntryList from "./MegaMenuEntryList.vue";
import { groupColumns, toGroups, useMegaMenu } from "./useMegaMenu";

// Width of every column in the menu; links that don't fit are truncated.
const COLUMN_WIDTH = "14rem";

const props = defineProps<{ item }>();
const emit = defineEmits<{ navigate: [] }>();

const { visibleEntries, drilldownEntries, featuredGroups, isDrilldown, entriesLabel, groupsLabel } = useMegaMenu(
  props.item
);

const activeLabel = ref<string>();
// Nothing is selected until an entry is hovered; its groups then stay until another entry is hovered.
const activeEntry = computed(() => drilldownEntries.value.find((entry) => entry.label === activeLabel.value));
const activeGroups = computed(() => toGroups(activeEntry.value?.items ?? [], "Events", 2));
const activeColumns = computed(() => activeGroups.value.reduce((total, group) => total + groupColumns(group), 0));
</script>
